import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { ButtonPrimary } from '@spotify-internal/encore-creator-web';
import {
  getWrappedButtonTheme,
  getWrappedMicrositeUrl,
  WRAPPED_BUTTON_COLOR_DEFAULT,
} from '../utils/wrapped';
import { getExternalPodcastId, logger } from '../../../lib/Api';
import tokens from '@spotify-internal/encore-foundation';
import { FormattedMessage } from 'react-intl';

type Props = { podcastId: string; podcastName: string; year: number };

export const WRAPPED_BANNER_TEST_ID = 'podcast-wrapped-banner';
export const WRAPPED_BUTTON_TEST_ID = 'podcast-wrapped-button';

export function WrappedBanner(props: Props) {
  const [externalPodcastId, setExternalPodcastId] = React.useState<null | string>(null);
  const { podcastId, podcastName, year } = props;

  React.useEffect(() => {
    (async function getHash() {
      try {
        const externalPodcastId = await getExternalPodcastId(podcastId);
        setExternalPodcastId(externalPodcastId);
      } catch {
        setExternalPodcastId(null);
      }
    })();
  }, [podcastId]);

  if (!externalPodcastId) {
    return null;
  }

  function logClick() {
    logger.wrappedBannerClick(podcastId);
  }

  return (
    <Banner data-testid={WRAPPED_BANNER_TEST_ID}>
      <TextContent>
        <FormattedMessage
          id="podcast.wrapped-title"
          defaultMessage="{year} Wrapped is here!"
          values={{ podcastName, year }}
        />
        <MobileLineBreak />{' '}
        <HighlightedText>
          <FormattedMessage
            id="podcast.wrapped-subtitle"
            defaultMessage="Take a look back at your year in podcasting."
            values={{ year }}
          />
        </HighlightedText>
      </TextContent>
      <WrappedLink
        href={getWrappedMicrositeUrl(externalPodcastId)}
        onClick={logClick}
        target="_blank"
        data-testid={WRAPPED_BUTTON_TEST_ID}
        rel="noopener noreferrer"
      >
        <FormattedMessage id="podcast.wrapped-button" defaultMessage="Get your Wrapped" />
      </WrappedLink>
    </Banner>
  );
}

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 4px;
  background-color: #000;
  color: #fff;
  padding: 18px 24px;
  margin-top: 32px;
  margin-bottom: 46px;

  @media (min-width: ${tokens.screenSmMin}) {
    align-items: center;
    flex-direction: row;
    margin-top: 0;
  }
`;

const MobileLineBreak = styled.br`
  display: block;

  @media (min-width: ${tokens.screenSmMin}) {
    display: none;
  }
`;

const TextContent = styled.p`
  font-weight: 600;
  margin-right: 16px;
  padding: 0;
  margin-bottom: 16px;

  @media (min-width: ${tokens.screenSmMin}) {
    margin-bottom: 0;
  }
`;

const HighlightedText = styled.span`
  color: ${WRAPPED_BUTTON_COLOR_DEFAULT};
`;

const LinkButton = styled(ButtonPrimary).attrs({
  forwardedAs: 'a',
  buttonSize: ButtonPrimary.sm,
})`
  width: 100%;

  @media (min-width: ${tokens.screenSmMin}) {
    width: inherit;
    margin-left: auto;
  }
`;

const WrappedLink = (props: any) => {
  return (
    <ThemeProvider theme={getWrappedButtonTheme()}>
      <LinkButton {...props} />
    </ThemeProvider>
  );
};
